import {AttributesMap} from '@packages/systems/wfdl';
import {VariantStateKey} from './VariantStateKey';

/**
 * A extension-point to the style system, the `VariantData` interface gives
 * styling control to the implementer.
 *
 * A `VariantData` entry connects the HTML renderer with the CSS renderer.
 *
 * ### Error handling
 *
 * The implementer of this interface is responsible for handling any recoverable
 * errors that occur within their implementation.
 *
 * Rather than prescribe a specific interface for handling errors, we recommend
 * the following pattern:
 *
 * - If any unexpected errors occur, report this error (e.g. with reportBug),
 *   then return the appropriate result.
 * - If `getClassName` or `getAttributes` results in an error, you may want to
 *   return `null`.
 * - If `getSelector` results in an error, you may want to return the original
 *   `baseSelector` argument unchanged.
 *
 * **Behavior of uncaught exceptions is undefined.** This may result in blocking
 * site publishing or cause a crash in the Designer, and the styles team
 * reserves the right to change this behavior in perpetuity.
 */
export interface VariantData {
  /**
   * If your variant needs to apply one or more class names to any applicable
   * elements, return a string here.
   *
   * - To apply multiple classes, return a string with class names separated by
   *   a space, e.g. `"foo bar baz"`.
   * - To apply no classes, return `null`.
   */
  getClassName(): string | null;
  /**
   * If your variant needs to apply any HTML attributes to any applicable
   * elements, return a POJO representation here.
   *
   * - To apply no attributes, return `null`.
   *
   * **Note**: ONLY use `getClassName` to apply `class`/`className` attributes.
   */
  getAttributes(): AttributesMap | null;
  /**
   * This method allows you to configure how your variant's CSS should be
   * applied in the style sheet.
   *
   * The incoming `baseSelector` argument corresponds to the _partial_ selector
   * already generated for your variant's application.
   *
   * Example:
   *
   * - The user sets the CSS `color: red` on the `Title` class whenever the
   *   `Flavor` variant is set to `Cherry`.
   * - Your variants are applied with a class in the style of `.flavor=cherry`
   *
   * In this case, the incoming selector would be something like `".title"`, but
   * could _also_ be something like `.title:hover`. You would probably do
   * something like `return baseSelector + ".flavor\\=cherry"` here (except your
   * code would derive the class rather than hard-code it).
   *
   * **Note**: It is your responsibility to modify this selector correctly, such
   * that the result is a valid CSS selector.
   */
  getSelector(baseSelector: string): string;
}

const __VARIANT_DATA_MAP_BRAND__: unique symbol = Symbol();

/**
 * A map for looking up `VariantData` based on any relevant `VariantStateKey`s.
 *
 * For instance, the CSS renderer uses this data structure to produce the
 * appropriate selectors when a variant is considered active in a given
 * element's context.
 */
export class VariantDataMap extends Map<VariantStateKey, VariantData> {
  [__VARIANT_DATA_MAP_BRAND__] = true;
}

export const emptyVariantDataMap = new VariantDataMap();
